import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, Users, Building2, Zap, Shield, Star, 
  Globe, MessageCircle, TrendingUp, Target, Award,
  Instagram, Youtube, Twitter, Sparkles, ChevronRight
} from 'lucide-react';

// Animated Wave Background SVG
const WaveBackground = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <svg className="absolute bottom-0 w-full h-[400px]" viewBox="0 0 1440 400" preserveAspectRatio="none">
      <defs>
        <linearGradient id="waveGrad1" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#F472B6" stopOpacity="0.3" />
          <stop offset="50%" stopColor="#FB923C" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#14B8A6" stopOpacity="0.3" />
        </linearGradient>
        <linearGradient id="waveGrad2" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#14B8A6" stopOpacity="0.2" />
          <stop offset="50%" stopColor="#F472B6" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#FB923C" stopOpacity="0.2" />
        </linearGradient>
      </defs>
      <path fill="url(#waveGrad1)" d="M0,200 C300,280 600,120 900,200 C1200,280 1350,160 1440,200 L1440,400 L0,400 Z">
        <animate attributeName="d" dur="10s" repeatCount="indefinite" values="
          M0,200 C300,280 600,120 900,200 C1200,280 1350,160 1440,200 L1440,400 L0,400 Z;
          M0,220 C300,140 600,280 900,180 C1200,100 1350,260 1440,180 L1440,400 L0,400 Z;
          M0,200 C300,280 600,120 900,200 C1200,280 1350,160 1440,200 L1440,400 L0,400 Z"
        />
      </path>
      <path fill="url(#waveGrad2)" d="M0,250 C400,180 800,320 1200,250 C1350,220 1400,280 1440,260 L1440,400 L0,400 Z">
        <animate attributeName="d" dur="8s" repeatCount="indefinite" values="
          M0,250 C400,180 800,320 1200,250 C1350,220 1400,280 1440,260 L1440,400 L0,400 Z;
          M0,280 C400,350 800,200 1200,280 C1350,310 1400,220 1440,290 L1440,400 L0,400 Z;
          M0,250 C400,180 800,320 1200,250 C1350,220 1400,280 1440,260 L1440,400 L0,400 Z"
        />
      </path>
    </svg>
  </div>
);

// Network Connection Visualization
const NetworkVisualization = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden">
    <svg className="w-full h-full" viewBox="0 0 800 600">
      <defs>
        <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#14B8A6" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#F472B6" stopOpacity="0.4" />
        </linearGradient>
      </defs>
      {/* Connection Lines */}
      <g className="opacity-30">
        <line x1="100" y1="100" x2="300" y2="200" stroke="url(#lineGrad)" strokeWidth="1" strokeDasharray="4 4">
          <animate attributeName="stroke-dashoffset" from="0" to="16" dur="2s" repeatCount="indefinite"/>
        </line>
        <line x1="300" y1="200" x2="500" y2="150" stroke="url(#lineGrad)" strokeWidth="1" strokeDasharray="4 4">
          <animate attributeName="stroke-dashoffset" from="0" to="16" dur="2s" repeatCount="indefinite"/>
        </line>
        <line x1="500" y1="150" x2="700" y2="250" stroke="url(#lineGrad)" strokeWidth="1" strokeDasharray="4 4">
          <animate attributeName="stroke-dashoffset" from="0" to="16" dur="2s" repeatCount="indefinite"/>
        </line>
        <line x1="200" y1="350" x2="400" y2="300" stroke="url(#lineGrad)" strokeWidth="1" strokeDasharray="4 4">
          <animate attributeName="stroke-dashoffset" from="0" to="16" dur="2s" repeatCount="indefinite"/>
        </line>
        <line x1="400" y1="300" x2="600" y2="400" stroke="url(#lineGrad)" strokeWidth="1" strokeDasharray="4 4">
          <animate attributeName="stroke-dashoffset" from="0" to="16" dur="2s" repeatCount="indefinite"/>
        </line>
      </g>
      {/* Nodes */}
      <g>
        <circle cx="100" cy="100" r="6" fill="#F472B6" opacity="0.6">
          <animate attributeName="r" values="6;8;6" dur="2s" repeatCount="indefinite"/>
        </circle>
        <circle cx="300" cy="200" r="8" fill="#14B8A6" opacity="0.7">
          <animate attributeName="r" values="8;10;8" dur="2.5s" repeatCount="indefinite"/>
        </circle>
        <circle cx="500" cy="150" r="6" fill="#FB923C" opacity="0.6">
          <animate attributeName="r" values="6;8;6" dur="1.8s" repeatCount="indefinite"/>
        </circle>
        <circle cx="700" cy="250" r="7" fill="#F472B6" opacity="0.6">
          <animate attributeName="r" values="7;9;7" dur="2.2s" repeatCount="indefinite"/>
        </circle>
        <circle cx="200" cy="350" r="5" fill="#14B8A6" opacity="0.5">
          <animate attributeName="r" values="5;7;5" dur="2s" repeatCount="indefinite"/>
        </circle>
        <circle cx="400" cy="300" r="9" fill="#FB923C" opacity="0.7">
          <animate attributeName="r" values="9;11;9" dur="2.3s" repeatCount="indefinite"/>
        </circle>
        <circle cx="600" cy="400" r="6" fill="#F472B6" opacity="0.6">
          <animate attributeName="r" values="6;8;6" dur="1.9s" repeatCount="indefinite"/>
        </circle>
      </g>
    </svg>
  </div>
);

// Floating Social Icons
const FloatingSocialIcon = ({ Icon, className, delay = 0 }) => (
  <div 
    className={`absolute p-3 rounded-xl bg-white/90 shadow-lg backdrop-blur-sm animate-float ${className}`}
    style={{ animationDelay: `${delay}s` }}
  >
    <Icon className="w-6 h-6 text-[#E8795A]" />
  </div>
);

// Decorative Star
const DecorativeStar = ({ className, color = '#F472B6', size = 12 }) => (
  <svg className={`absolute ${className}`} width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M12 0L14.59 8.41L23 12L14.59 15.59L12 24L9.41 15.59L1 12L9.41 8.41L12 0Z" />
  </svg>
);

// Feature Card Component
const FeatureCard = ({ icon: Icon, title, description, color }) => (
  <div className="vibrant-card p-6 group hover:scale-[1.02] transition-all duration-300">
    <div className={`w-14 h-14 rounded-2xl ${color} flex items-center justify-center mb-4 
      group-hover:scale-110 transition-transform duration-300`}>
      <Icon className="w-7 h-7 text-white" />
    </div>
    <h3 className="font-bold text-lg text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
  </div>
);

// Role Selection Card
const RoleCard = ({ icon: Icon, title, description, features, onClick, accent, testId }) => (
  <div 
    onClick={onClick}
    className={`relative p-8 rounded-3xl cursor-pointer group transition-all duration-500
      bg-white border-2 border-transparent hover:border-${accent} overflow-hidden`}
    style={{
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.06)'
    }}
    data-testid={testId}
  >
    {/* Gradient Overlay on Hover */}
    <div className={`absolute inset-0 bg-gradient-to-br ${
      accent === '[#E8795A]' ? 'from-[#E8795A]/5 to-[#FB923C]/5' : 'from-[#14B8A6]/5 to-[#3AAFB9]/5'
    } opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
    
    <div className="relative z-10">
      <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${
        accent === '[#E8795A]' ? 'from-[#E8795A] to-[#FB923C]' : 'from-[#14B8A6] to-[#3AAFB9]'
      } flex items-center justify-center mb-6 shadow-xl group-hover:scale-110 transition-transform duration-300`}>
        <Icon className="w-10 h-10 text-white" />
      </div>
      
      <h3 className="text-2xl font-bold text-gray-900 mb-3">{title}</h3>
      <p className="text-gray-600 mb-6">{description}</p>
      
      <ul className="space-y-3 mb-6">
        {features.map((feature, i) => (
          <li key={i} className="flex items-center gap-3 text-sm text-gray-700">
            <div className={`w-5 h-5 rounded-full ${
              accent === '[#E8795A]' ? 'bg-[#E8795A]/10 text-[#E8795A]' : 'bg-[#14B8A6]/10 text-[#14B8A6]'
            } flex items-center justify-center`}>
              <ChevronRight className="w-3 h-3" />
            </div>
            {feature}
          </li>
        ))}
      </ul>
      
      <Button 
        className={`w-full h-12 rounded-xl font-semibold ${
          accent === '[#E8795A]' 
            ? 'bg-gradient-to-r from-[#E8795A] to-[#FB923C] hover:shadow-lg hover:shadow-[#E8795A]/30' 
            : 'bg-gradient-to-r from-[#14B8A6] to-[#3AAFB9] hover:shadow-lg hover:shadow-[#14B8A6]/30'
        } text-white transition-all duration-300`}
      >
        Get Started as {title}
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    </div>
  </div>
);

const Landing = () => {
  const navigate = useNavigate();
  const [showRoleSelection, setShowRoleSelection] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Logo URL
  const logoUrl = "https://customer-assets.emergentagent.com/job_creator-brand-2/artifacts/feyyw8lo_145437-200.png";

  const handleLetsStart = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setShowRoleSelection(true);
      setIsTransitioning(false);
    }, 400);
  };

  const handleBack = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setShowRoleSelection(false);
      setIsTransitioning(false);
    }, 400);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAFBFC] to-white overflow-hidden relative">
      {/* Creative Background Elements */}
      <div className="fixed inset-0 pointer-events-none">
        {/* Vibrant Orbs */}
        <div className="creator-orb creator-orb-pink" style={{ top: '-10%', right: '10%' }} />
        <div className="creator-orb creator-orb-orange" style={{ bottom: '20%', left: '-5%' }} />
        <div className="creator-orb creator-orb-teal" style={{ top: '40%', right: '-10%' }} />
        
        {/* Network Lines */}
        <NetworkVisualization />
        
        {/* Decorative Stars */}
        <DecorativeStar className="top-[15%] left-[15%] animate-pulse" color="#F472B6" size={16} />
        <DecorativeStar className="top-[25%] right-[20%] animate-pulse" color="#FB923C" size={12} style={{ animationDelay: '0.5s' }} />
        <DecorativeStar className="bottom-[30%] left-[25%] animate-pulse" color="#14B8A6" size={14} style={{ animationDelay: '1s' }} />
        <DecorativeStar className="top-[60%] right-[15%] animate-pulse" color="#F472B6" size={10} style={{ animationDelay: '1.5s' }} />
      </div>
      
      {/* Flowing Waves at Bottom */}
      <WaveBackground />

      {/* Floating Social Icons */}
      <FloatingSocialIcon Icon={Instagram} className="top-[20%] left-[8%] hidden lg:block" delay={0} />
      <FloatingSocialIcon Icon={Youtube} className="top-[35%] right-[10%] hidden lg:block" delay={0.5} />
      <FloatingSocialIcon Icon={Twitter} className="bottom-[35%] left-[12%] hidden lg:block" delay={1} />

      {/* Navigation */}
      <nav className="relative z-50 px-6 lg:px-12 py-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logoUrl} alt="CmdOut" className="w-10 h-10" />
            <span className="text-2xl font-bold gradient-text-brand">CmdOut</span>
          </div>
          <Button 
            onClick={() => navigate('/login')}
            variant="ghost"
            className="font-semibold"
            data-testid="nav-login-btn"
          >
            Login
          </Button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="relative z-10 px-6 lg:px-12">
        <div className={`max-w-7xl mx-auto transition-all duration-500 ${isTransitioning ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
          
          {!showRoleSelection ? (
            /* Hero Section */
            <div className="pt-12 lg:pt-20 pb-32">
              <div className="max-w-4xl mx-auto text-center">
                {/* Badge */}
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#F472B6]/10 to-[#14B8A6]/10 border border-[#14B8A6]/20 mb-8">
                  <Zap className="w-4 h-4 text-[#E8795A]" />
                  <span className="text-sm font-medium text-gray-700">The Micro-Creator Economy Platform</span>
                </div>

                {/* Main Headline */}
                <h1 className="text-5xl lg:text-7xl font-extrabold leading-tight mb-6">
                  <span className="gradient-text-creator">Unite</span>
                  <span className="text-gray-900">, </span>
                  <span className="gradient-text-brand">Collaborate</span>
                  <span className="text-gray-900">, </span>
                  <span className="bg-gradient-to-r from-[#F472B6] to-[#E879F9] bg-clip-text text-transparent">Thrive</span>
                </h1>

                <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-10 leading-relaxed">
                  Connect with authentic micro-creators who deliver <span className="font-semibold text-[#E8795A]">6.7x higher engagement</span> than mega influencers. Built for the new era of creator marketing.
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    onClick={handleLetsStart}
                    className="h-14 px-8 text-lg font-semibold rounded-2xl bg-gradient-to-r from-[#14B8A6] to-[#3AAFB9] hover:shadow-xl hover:shadow-[#14B8A6]/30 transition-all duration-300"
                    data-testid="get-started-btn"
                  >
                    Get Started Free
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/login')}
                    className="h-14 px-8 text-lg font-semibold rounded-2xl border-2 hover:bg-gray-50"
                    data-testid="login-btn"
                  >
                    Sign In
                  </Button>
                </div>

                {/* Trust Indicators */}
                <div className="flex items-center justify-center gap-8 mt-12 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-[#14B8A6]" />
                    <span>Secure Escrow</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-[#E8795A]" />
                    <span>12+ Countries</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-[#F472B6]" />
                    <span>AI Matching</span>
                  </div>
                </div>
              </div>

              {/* Features Grid */}
              <div className="grid md:grid-cols-3 gap-6 mt-24 max-w-5xl mx-auto">
                <FeatureCard 
                  icon={Target}
                  title="Smart Matching"
                  description="AI-powered creator discovery based on engagement metrics, not just follower counts."
                  color="bg-gradient-to-br from-[#14B8A6] to-[#3AAFB9]"
                />
                <FeatureCard 
                  icon={TrendingUp}
                  title="Match Score"
                  description="Transparent creator rankings with engagement quality, niche authority, and authenticity scores."
                  color="bg-gradient-to-br from-[#E8795A] to-[#FB923C]"
                />
                <FeatureCard 
                  icon={MessageCircle}
                  title="Collaboration OS"
                  description="End-to-end campaign management with contracts, deliverables, and milestone payments."
                  color="bg-gradient-to-br from-[#F472B6] to-[#E879F9]"
                />
              </div>
            </div>
          ) : (
            /* Role Selection Section */
            <div className="py-16 lg:py-24">
              <div className="text-center mb-12">
                <button 
                  onClick={handleBack}
                  className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6 transition-colors"
                  data-testid="back-btn"
                >
                  <ChevronRight className="w-4 h-4 rotate-180" />
                  Back
                </button>
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                  Choose Your Path
                </h2>
                <p className="text-xl text-gray-600">
                  Select how you want to use CmdOut
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                <RoleCard 
                  icon={Users}
                  title="Creator"
                  description="Monetize your influence and connect with brands that align with your values."
                  features={[
                    'Get discovered by your Match Score',
                    'Apply to campaigns in your niche',
                    'Secure payments with escrow',
                    'Track your analytics & growth'
                  ]}
                  accent="[#E8795A]"
                  onClick={() => navigate('/login?role=creator')}
                  testId="creator-role-btn"
                />
                <RoleCard 
                  icon={Building2}
                  title="Brand"
                  description="Find authentic creators who truly connect with your target audience."
                  features={[
                    'AI-powered creator matching',
                    'Micro-creator first discovery',
                    'Campaign management tools',
                    'Transparent ROI tracking'
                  ]}
                  accent="[#14B8A6]"
                  onClick={() => navigate('/login?role=brand')}
                  testId="brand-role-btn"
                />
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-gray-100 mt-auto">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src={logoUrl} alt="CmdOut" className="w-8 h-8" />
              <span className="font-semibold text-gray-700">CmdOut.io</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <a href="/privacy" className="hover:text-gray-700 transition-colors">Privacy</a>
              <a href="/terms" className="hover:text-gray-700 transition-colors">Terms</a>
              <a href="/contact" className="hover:text-gray-700 transition-colors">Contact</a>
            </div>
            <p className="text-sm text-gray-400">© 2026 CmdOut. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
